import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Button,
  Tabs,
  Tab,
  Badge,
  Modal,
  Form,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faExpand,
  faCompress,
  faUsers,
  faBuilding,
  faHandshake,
  faClipboardList,
  faChevronLeft,
  faChevronRight,
  faExchangeAlt,
  faUsersCog,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';
import api from 'services/api';
import { toast } from 'react-toastify';
import './MeetingsCard.css'; // Assurez-vous d'avoir ce fichier CSS pour les styles personnalisés
import CompanyTab from './MeetingCard/CompanyTab';
import PartnerTab from './MeetingCard/PartnerTab';
import ListTab from './MeetingCard/ListTab';
import AnalysisTab from './MeetingCard/AnalysisTab';
import ReplacementTab from './MeetingCard/ReplacementTab';
import StatsTab from './MeetingCard/StatsTab';

const formatTime = timeStr => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

const formatParticipantName = p => {
  if (!p) return 'N/A';
  let mainName =
    `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Participant Inconnu';
  if (p.is_replaced) {
    const replacementName = `${p.replacement_firstname || ''} ${
      p.replacement_lastname || ''
    }`.trim();
    return (
      <span>
        <span style={{ textDecoration: 'line-through' }}>{mainName}</span>{' '}
        <FontAwesomeIcon
          icon={faExchangeAlt}
          className="text-muted"
          title={`Remplacé par ${replacementName || 'N/A'}`}
        />{' '}
        <span className="text-info">{replacementName || 'N/A'}</span>
      </span>
    );
  }
  return mainName;
};

const formatParticipantNameForSelect = (p, entityType) => {
  if (!p) return 'N/A';
  let mainName =
    `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Participant Inconnu';
  // Le nom de l'entité (entreprise/partenaire) est déjà géré par <optgroup>
  // On peut l'ajouter si on veut le répéter dans l'option elle-même, mais ce n'est pas strictement nécessaire avec optgroup.
  // const entityName = entityType === 'company' ? (p.company?.name || '') : (p.partner?.name || '');
  // const entityDisplay = entityName ? ` (${entityName})` : '';

  if (p.is_replaced) {
    const replacementName =
      `${p.replacement_firstname || ''} ${
        p.replacement_lastname || ''
      }`.trim() || 'Remplaçant';
    return `${mainName} (Remplacé par ${replacementName})`;
  }
  return `${mainName}`; // ${entityDisplay} si vous voulez le nom de l'entité dans l'option
};

const generateSalonTimeSlots = salon => {
  if (
    !salon.date ||
    !salon.start_time ||
    !salon.end_time ||
    !salon.slot_duration
  ) {
    return [];
  }

  const slots = [];
  const salonDate = salon.date.substring(0, 10);

  let currentTime = new Date(`${salonDate}T${salon.start_time}`);
  const endTime = new Date(`${salonDate}T${salon.end_time}`);
  const lunchStart = salon.lunch_break_start_time
    ? new Date(`${salonDate}T${salon.lunch_break_start_time}`)
    : null;
  const lunchEnd =
    lunchStart && salon.lunch_break_duration
      ? new Date(lunchStart.getTime() + salon.lunch_break_duration * 60000)
      : null;

  while (currentTime < endTime) {
    const slotStartTime = new Date(currentTime);
    const slotEndTime = new Date(
      currentTime.getTime() + salon.slot_duration * 60000
    );

    let isLunchBreak = false;
    if (lunchStart && lunchEnd) {
      if (
        (slotStartTime >= lunchStart && slotStartTime < lunchEnd) ||
        (slotEndTime > lunchStart && slotEndTime <= lunchEnd) ||
        (slotStartTime < lunchStart && slotEndTime > lunchEnd)
      ) {
        isLunchBreak = true;
      }
    }

    if (!isLunchBreak && slotEndTime <= endTime) {
      slots.push({
        start: formatTime(slotStartTime.toTimeString()),
        end: formatTime(slotEndTime.toTimeString())
      });
    }

    currentTime = new Date(currentTime.getTime() + salon.slot_duration * 60000);

    if (
      lunchStart &&
      lunchEnd &&
      currentTime >= lunchStart &&
      currentTime < lunchEnd
    ) {
      currentTime = new Date(lunchEnd);
    }
  }
  return slots;
};

const SLOT_COLUMN_WIDTH = 250;

const MeetingsCard = ({ salon }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  const [showParticipantEditModal, setShowParticipantEditModal] =
    useState(false);
  const [editingMeetingDetails, setEditingMeetingDetails] = useState(null);
  const [selectedModalCompanyParticipant, setSelectedModalCompanyParticipant] =
    useState('');
  const [selectedModalPartnerParticipant, setSelectedModalPartnerParticipant] =
    useState('');
  const [isSavingMeeting, setIsSavingMeeting] = useState(false);
  const [showCreateMeetingModal, setShowCreateMeetingModal] = useState(false);
  const [creatingMeetingDetails, setCreatingMeetingDetails] = useState(null); // { slotStart, slotEnd }
  const [newMeetingCompanyP, setNewMeetingCompanyP] = useState('');
  const [newMeetingPartnerP, setNewMeetingPartnerP] = useState('');
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [meetingsData, setMeetingsData] = useState({
    meetings: [],
    company_participants: [],
    partner_participants: [],
    unmet_company_wishes: [],
    unmet_partner_wishes: [],
    participant_conflicts: []
  });
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(true);
  const salonId = salon?.id || null; // Assurez-vous que salon est défini avant d'accéder à l'ID

  const fetchMeetingsData = useCallback(async () => {
    if (!salonId) {
      setIsLoadingMeetings(false); // S'assurer que le chargement s'arrête
      return;
    }
    setIsLoadingMeetings(true);
    try {
      const response = await api.get(
        `/admin/partner-salons/${salonId}/meetings-data`
      );
      if (response.data.success) {
        setMeetingsData(response.data.data);
      } else {
        toast('Erreur lors du chargement des données des rendez-vous.');
        setMeetingsData({
          // Réinitialiser en cas d'erreur pour éviter d'afficher des données obsolètes
          meetings: [],
          company_participants: [],
          partner_participants: [],
          unmet_company_wishes: [],
          unmet_partner_wishes: [],
          participant_conflicts: []
        });
      }
    } catch (error) {
      console.error('Error fetching meetings data:', error);
      toast(
        'Une erreur est survenue lors du chargement des données des rendez-vous.'
      );
      setMeetingsData({
        // Réinitialiser en cas d'erreur
        meetings: [],
        company_participants: [],
        partner_participants: [],
        unmet_company_wishes: [],
        unmet_partner_wishes: [],
        participant_conflicts: []
      });
    } finally {
      setIsLoadingMeetings(false);
    }
  }, [salonId]); // salonId est la dépendance clé ici

  useEffect(() => {
    fetchMeetingsData();
  }, [fetchMeetingsData]);

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const dynamicTabContentContainerStyle = isFs => {
    if (isFs) {
      return {
        overflow: 'auto',
        height: 'calc(100vh - 170px)'
      };
    }
    return {
      overflow: 'auto',
      maxHeight: '500px'
    };
  };

  const timeSlots = useMemo(
    () => generateSalonTimeSlots(salon),
    [
      salon.date,
      salon.start_time,
      salon.end_time,
      salon.slot_duration,
      salon.lunch_break_start_time,
      salon.lunch_break_duration
    ]
  );

  const companyParticipantsByCompany = useMemo(() => {
    return meetingsData.company_participants?.reduce((acc, p) => {
      const companyName = p.company?.name || 'Entreprise inconnue';
      if (!acc[companyName]) {
        acc[companyName] = [];
      }
      acc[companyName].push(p);
      return acc;
    }, {});
  }, [meetingsData.company_participants]);

  const partnerParticipantsByPartner = useMemo(() => {
    return meetingsData.partner_participants?.reduce((acc, p) => {
      const partnerName = p.partner?.name || 'Partenaire inconnu';
      if (!acc[partnerName]) {
        acc[partnerName] = [];
      }
      acc[partnerName].push(p);
      return acc;
    }, {});
  }, [meetingsData.partner_participants]);

  const findMeetingById = meetingId => {
    return meetingsData.meetings?.find(m => m.id === meetingId);
  };

  const handleCellClick = (
    meetingCellData,
    slot,
    participantIdForRow = null, // Nouvel argument optionnel
    viewTypeForRow = null // Nouvel argument optionnel ('company' ou 'partner')
  ) => {
    if (meetingCellData && meetingCellData.meetingId) {
      // Logique existante pour modifier un RDV
      const fullMeeting = findMeetingById(meetingCellData.meetingId);
      if (fullMeeting) {
        setEditingMeetingDetails({
          meetingId: fullMeeting.id,
          slotStart: formatTime(fullMeeting.start_time),
          currentCompanyPId: fullMeeting.company_participants?.[0]?.id || null,
          currentPartnerPId: fullMeeting.partner_participants?.[0]?.id || null
        });
        setSelectedModalCompanyParticipant(
          fullMeeting.company_participants?.[0]?.id?.toString() || ''
        );
        setSelectedModalPartnerParticipant(
          fullMeeting.partner_participants?.[0]?.id?.toString() || ''
        );
        setShowParticipantEditModal(true);
      }
    } else if (slot) {
      // Case vide : ouvrir le modal de création
      setCreatingMeetingDetails({
        slotStart: slot.start,
        slotEnd: slot.end
      });

      // Pré-remplir en fonction du clic
      let initialCompanyP = '';
      let initialPartnerP = '';

      if (participantIdForRow && viewTypeForRow) {
        if (viewTypeForRow === 'company') {
          initialCompanyP = participantIdForRow.toString();
        } else if (viewTypeForRow === 'partner') {
          initialPartnerP = participantIdForRow.toString();
        }
      }

      setNewMeetingCompanyP(initialCompanyP);
      setNewMeetingPartnerP(initialPartnerP);
      setShowCreateMeetingModal(true);
    }
  };

  const handleCreateMeeting = async () => {
    if (
      !creatingMeetingDetails ||
      (!newMeetingCompanyP && !newMeetingPartnerP)
    ) {
      toast.warn('Veuillez sélectionner au moins un participant.');
      return;
    }
    setIsCreatingMeeting(true);
    try {
      const payload = {
        start_time: creatingMeetingDetails.slotStart,
        end_time: creatingMeetingDetails.slotEnd,
        company_participant_id: newMeetingCompanyP
          ? parseInt(newMeetingCompanyP)
          : null,
        partner_participant_id: newMeetingPartnerP
          ? parseInt(newMeetingPartnerP)
          : null
      };
      const response = await api.post(
        `/admin/partner-salons/${salonId}/meetings`,
        payload
      );
      if (response.data.success) {
        toast('Rendez-vous créé avec succès !');
        setMeetingsData(response.data.data);
        setShowCreateMeetingModal(false);
        setCreatingMeetingDetails(null);
        setNewMeetingCompanyP('');
        setNewMeetingPartnerP('');
      } else {
        toast(response.data.message || 'Erreur lors de la création du RDV.');
      }
    } catch (error) {
      console.error('Erreur création RDV:', error);
      toast(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la création du RDV.'
      );
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  const handleSaveMeetingParticipants = async () => {
    if (!editingMeetingDetails || isSavingMeeting) return;
    setIsSavingMeeting(true);
    try {
      const payload = {
        new_company_participant_id: selectedModalCompanyParticipant
          ? parseInt(selectedModalCompanyParticipant)
          : null,
        new_partner_participant_id: selectedModalPartnerParticipant
          ? parseInt(selectedModalPartnerParticipant)
          : null
      };
      const response = await api.put(
        `/admin/partner-salons/${salonId}/meetings/${editingMeetingDetails.meetingId}/participants`,
        payload
      );
      if (response.data.success) {
        toast('Rendez-vous mis à jour avec succès !');
        setMeetingsData(response.data.data);
        setShowParticipantEditModal(false);
        setEditingMeetingDetails(null);
      } else {
        toast(
          response.data.message ||
            'Erreur lors de la mise à jour du rendez-vous.'
        );
      }
    } catch (error) {
      console.error(
        'Erreur lors de la mise à jour des participants du RDV:',
        error
      );
      const errorMessage =
        error.response?.data?.message ||
        'Une erreur est survenue lors de la mise à jour.';
      toast(errorMessage);
    } finally {
      setIsSavingMeeting(false);
    }
  };

  const handleListMeetingClick = rawMeeting => {
    if (rawMeeting) {
      const meetingCellData = {
        meetingId: rawMeeting.id,
        fullMeetingData: rawMeeting
      };
      const slotData = {
        start: formatTime(rawMeeting.start_time),
        end: formatTime(rawMeeting.end_time)
      };
      handleCellClick(meetingCellData, slotData);
    }
  };

  const [activeView, setActiveView] = useState('company-view'); // Assurez-vous que c'est bien initialisé
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const companyScrollContainerRef = useRef(null);
  const partnerScrollContainerRef = useRef(null);

  ////////

  const updateScrollButtonStates = useCallback(containerElement => {
    if (!containerElement) {
      console.log('DEBUG: No container element in updateScrollButtonStates'); // Ligne de débogage
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = containerElement;
    // console.log(`DEBUG: scrollLeft: ${scrollLeft}, scrollWidth: ${scrollWidth}, clientWidth: ${clientWidth}`); // Ligne de débogage

    const isScrollable = scrollWidth > clientWidth + 1; // +1 pour une petite marge
    // console.log('DEBUG: isScrollable:', isScrollable); // Ligne de débogage

    if (!isScrollable) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const newCanScrollLeft = scrollLeft > 5; // Mettre un petit seuil pour éviter la sensibilité extrême
    const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 5; // Idem
    // console.log(`DEBUG: Setting canScrollLeft: ${newCanScrollLeft}, canScrollRight: ${newCanScrollRight}`); // Ligne de débogage

    setCanScrollLeft(newCanScrollLeft);
    setCanScrollRight(newCanScrollRight);
  }, []);

  useLayoutEffect(() => {
    const getActiveContainer = () => {
      if (activeView === 'company-view')
        return companyScrollContainerRef.current;
      if (activeView === 'partner-view')
        return partnerScrollContainerRef.current;
      return null;
    };

    const activeContainer = getActiveContainer();
    if (activeContainer) {
      const checkAndUpdateButtons = () => {
        // console.log('DEBUG: checkAndUpdateButtons called. isFullScreen:', isFullScreen);
        updateScrollButtonStates(activeContainer);
      };

      // Cet appel est crucial. Il sera ré-exécuté si activeView, les données,
      // ou isFullScreen changent.
      checkAndUpdateButtons();

      activeContainer.addEventListener('scroll', checkAndUpdateButtons, {
        passive: true
      });

      const observer = new ResizeObserver(checkAndUpdateButtons);
      observer.observe(activeContainer);
      if (
        activeContainer.firstChild &&
        activeContainer.firstChild instanceof HTMLElement
      ) {
        observer.observe(activeContainer.firstChild);
      }

      return () => {
        activeContainer.removeEventListener('scroll', checkAndUpdateButtons);
        observer.disconnect();
      };
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }
  }, [activeView, updateScrollButtonStates, isFullScreen]);

  const handleHorizontalScroll = direction => {
    let containerRef;
    if (activeView === 'company-view') {
      containerRef = companyScrollContainerRef;
    } else if (activeView === 'partner-view') {
      containerRef = partnerScrollContainerRef;
    } else {
      return;
    }

    if (containerRef && containerRef.current) {
      const currentScrollLeft = containerRef.current.scrollLeft;
      let targetScrollLeft;

      if (direction === 'right') {
        // Pour aller au créneau suivant, on veut s'aligner sur le prochain multiple de SLOT_COLUMN_WIDTH
        // qui est supérieur au scrollLeft actuel.
        targetScrollLeft =
          (Math.floor(currentScrollLeft / SLOT_COLUMN_WIDTH) + 1) *
          SLOT_COLUMN_WIDTH;
      } else {
        // direction === 'left'
        // Pour aller au créneau précédent, on veut s'aligner sur le multiple de SLOT_COLUMN_WIDTH
        // qui est inférieur ou égal au scrollLeft actuel moins une petite marge (pour "passer" le créneau actuel).
        targetScrollLeft =
          (Math.ceil(currentScrollLeft / SLOT_COLUMN_WIDTH) - 1) *
          SLOT_COLUMN_WIDTH;
      }

      // S'assurer de ne pas dépasser les limites
      const maxScroll =
        containerRef.current.scrollWidth - containerRef.current.clientWidth;
      if (targetScrollLeft > maxScroll) {
        targetScrollLeft = maxScroll;
      }
      if (targetScrollLeft < 0) {
        targetScrollLeft = 0;
      }

      containerRef.current.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const renderScrollButtons = () => {
    if (activeView !== 'company-view' && activeView !== 'partner-view') {
      return null;
    }

    // console.log(`DEBUG: renderScrollButtons. canScrollLeft: ${canScrollLeft}, canScrollRight: ${canScrollRight}`);

    // Toujours rendre les boutons pour les vues concernées.
    // Leur état 'disabled' sera géré par canScrollLeft et canScrollRight.
    return (
      <div className="d-flex justify-content-end mb-2 mt-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => handleHorizontalScroll('left')}
          disabled={!canScrollLeft} // La désactivation dépend toujours de l'état
          className="me-2"
          aria-label="Défiler à gauche"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => handleHorizontalScroll('right')}
          disabled={!canScrollRight} // La désactivation dépend toujours de l'état
          aria-label="Défiler à droite"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>
    );
  };

  ////////

  const unmetCompanyWishes = meetingsData.unmet_company_wishes || [];
  const unmetPartnerWishes = meetingsData.unmet_partner_wishes || [];
  const participantConflicts = meetingsData.participant_conflicts || [];

  const totalIssuesAndUnmetWishes =
    participantConflicts.length +
    unmetCompanyWishes.length +
    unmetPartnerWishes.length;

  const stickyHeaderCellStyle = {
    position: 'sticky',
    zIndex: 2,
    backgroundColor: 'var(--falcon-200)'
  };

  const stickyBodyCellStyle = {
    position: 'sticky',
    zIndex: 1,
    backgroundColor: 'var(--falcon-body-bg)'
  };

  const handleFinalizePlanning = async () => {
    if (
      !window.confirm(
        'Êtes-vous sûr de vouloir valider définitivement le planning ? Cette action est irréversible et enverra un e-mail de confirmation à tous les participants avec leur planning.'
      )
    ) {
      return;
    }

    try {
      const response = await api.post(
        `/admin/partner-salons/${salonId}/finalize-planning`
      );
      if (response.data.success) {
        toast('Le planning a été validé définitivement.');
        window.location.reload(); // Recharger la page
      } else {
        toast(response.data.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      console.error('Erreur lors de la validation du planning:', error);
      toast(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la validation du planning.'
      );
    }
  };

  if (isLoadingMeetings) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 mb-0">Chargement des données des rendez-vous...</p>
        </Card.Body>
      </Card>
    );
  }

  const cardContent = (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Rendez-vous</h5>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={toggleFullScreen}
            title={isFullScreen ? 'Quitter le plein écran' : 'Plein écran'}
            className="me-2"
          >
            <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
          </Button>
          {/* Afficher le bouton seulement si la période de fin des vœux est terminée */}
          {salon?.registration_end_date &&
            new Date(salon.registration_end_date) <= new Date() && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleFinalizePlanning}
                title="Valider définitivement le planning"
                disabled={salon?.is_planning_finalized} // Désactiver si déjà validé
              >
                <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                {salon?.is_planning_finalized
                  ? 'Planning validé'
                  : 'Valider planning'}
              </Button>
            )}
        </div>
      </div>

      {(!meetingsData.meetings || meetingsData.meetings.length === 0) && (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            size="3x"
            className="text-muted mb-3"
          />
          <p className="text-muted">
            {/* Utiliser salonBaseData pour les conditions sur can_schedule et closing_date */}
            {salon?.can_schedule === false &&
            salon?.closing_date &&
            new Date(salon.closing_date) > new Date()
              ? `La planification des rendez-vous sera possible après la date de fermeture des inscriptions (${new Date(
                  salon.closing_date
                ).toLocaleDateString('fr-FR')}).`
              : 'Aucun rendez-vous planifié pour ce salon.'}
          </p>
          {(!meetingsData.meetings || meetingsData.meetings.length === 0) &&
            salon?.can_schedule !== false && (
              <p className="text-muted small">
                Cliquez sur "
                {meetingsData.meetings && meetingsData.meetings.length > 0
                  ? 'Re-planifier'
                  : 'Planifier'}
                " pour générer les rendez-vous.
              </p>
            )}
        </div>
      )}

      {meetingsData.meetings && meetingsData.meetings.length > 0 && (
        <Tabs
          activeKey={activeView}
          onSelect={k => setActiveView(k)}
          id="meetings-tabs"
          className="mt-3"
          unmountOnExit={false}
        >
          <Tab
            eventKey="company-view"
            title={
              <>
                <FontAwesomeIcon icon={faBuilding} className="me-1" /> Vue
                Entreprises
              </>
            }
          >
            {renderScrollButtons()}
            <div
              ref={companyScrollContainerRef}
              className="mt-3"
              style={dynamicTabContentContainerStyle(isFullScreen)}
            >
              <CompanyTab
                meetings={meetingsData.meetings}
                companyParticipants={meetingsData.company_participants}
                timeSlots={timeSlots}
                stickyHeaderCellStyle={stickyHeaderCellStyle}
                stickyBodyCellStyle={stickyBodyCellStyle}
                handleCellClick={handleCellClick}
                formatTime={formatTime}
                formatParticipantName={formatParticipantName}
              />
            </div>
          </Tab>

          <Tab
            eventKey="partner-view"
            title={
              <>
                <FontAwesomeIcon icon={faUsers} className="me-1" /> Vue
                Partenaires
              </>
            }
          >
            {renderScrollButtons()}
            <div
              ref={partnerScrollContainerRef}
              className="mt-3"
              style={dynamicTabContentContainerStyle(isFullScreen)}
            >
              <PartnerTab
                meetings={meetingsData.meetings}
                partnerParticipants={meetingsData.partner_participants}
                timeSlots={timeSlots}
                stickyHeaderCellStyle={stickyHeaderCellStyle}
                stickyBodyCellStyle={stickyBodyCellStyle}
                handleCellClick={handleCellClick}
                formatTime={formatTime}
                formatParticipantName={formatParticipantName}
              />
            </div>
          </Tab>

          <Tab
            eventKey="list-view"
            title={
              <>
                <FontAwesomeIcon icon={faHandshake} className="me-1" /> Liste
                Complète
              </>
            }
          >
            <div
              className="mt-3"
              style={dynamicTabContentContainerStyle(isFullScreen)}
            >
              <ListTab
                meetings={meetingsData.meetings}
                timeSlots={timeSlots}
                handleListMeetingClick={handleListMeetingClick}
                formatTime={formatTime}
                formatParticipantName={formatParticipantName}
              />
            </div>
          </Tab>

          <Tab
            eventKey="analysis-tab" // Clé de l'onglet modifiée
            title={
              <>
                <FontAwesomeIcon
                  icon={faClipboardList} // Icône modifiée
                  className="me-1"
                />
                Planification {/* Titre modifié */}
                {totalIssuesAndUnmetWishes > 0 && (
                  <Badge pill bg="warning" text="dark" className="ms-1">
                    {totalIssuesAndUnmetWishes}
                  </Badge>
                )}
              </>
            }
          >
            <div
              className="mt-3"
              style={dynamicTabContentContainerStyle(isFullScreen)}
            >
              <AnalysisTab
                meetingsData={meetingsData}
                formatParticipantName={formatParticipantName}
              />
            </div>
          </Tab>
          <Tab
            eventKey="replacement-view"
            title={
              <>
                <FontAwesomeIcon icon={faUsersCog} className="me-1" />{' '}
                Remplacements
              </>
            }
          >
            <ReplacementTab
              salon={{
                id: salonId,
                name: salon.name,
                company_participants: meetingsData.company_participants,
                partner_participants: meetingsData.partner_participants
              }}
              onParticipantReplaced={updatedDataFromApi => {
                setMeetingsData(updatedDataFromApi); // Mettre à jour les données des meetings
              }}
              dynamicTabContentContainerStyle={dynamicTabContentContainerStyle}
              isFullScreen={isFullScreen}
            />
          </Tab>
          <Tab
            eventKey="stats-view"
            title={
              <>
                <FontAwesomeIcon icon={faChartBar} className="me-1" />{' '}
                Statistiques
              </>
            }
          >
            <div
              className="mt-3"
              style={dynamicTabContentContainerStyle(isFullScreen)}
            >
              <StatsTab meetingsData={meetingsData} salonId={salon.id} />
            </div>
          </Tab>
        </Tabs>
      )}
    </>
  );

  const modalsContent = (
    <>
      {editingMeetingDetails && (
        <Modal
          show={showParticipantEditModal}
          onHide={() => setShowParticipantEditModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Modifier RDV - {editingMeetingDetails.slotStart}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Participant Entreprise</Form.Label>
              <Form.Select
                value={selectedModalCompanyParticipant}
                onChange={e =>
                  setSelectedModalCompanyParticipant(e.target.value)
                }
              >
                <option value="">-- Aucun --</option>
                {companyParticipantsByCompany &&
                  Object.entries(companyParticipantsByCompany).map(
                    ([companyName, participants]) => (
                      <optgroup label={companyName} key={companyName}>
                        {participants.map(p => (
                          <option key={`comp-opt-${p.id}`} value={p.id}>
                            {formatParticipantNameForSelect(p, 'company')}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Participant Partenaire</Form.Label>
              <Form.Select
                value={selectedModalPartnerParticipant}
                onChange={e =>
                  setSelectedModalPartnerParticipant(e.target.value)
                }
              >
                <option value="">-- Aucun --</option>
                {partnerParticipantsByPartner &&
                  Object.entries(partnerParticipantsByPartner).map(
                    ([partnerName, participants]) => (
                      <optgroup label={partnerName} key={partnerName}>
                        {participants.map(p => (
                          <option key={`part-opt-${p.id}`} value={p.id}>
                            {formatParticipantNameForSelect(p, 'partner')}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowParticipantEditModal(false)}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveMeetingParticipants}
              disabled={isSavingMeeting || salon?.is_planning_finalized} // Désactiver si en cours d'enregistrement ou si le planning est validé
            >
              {isSavingMeeting
                ? 'Enregistrement...'
                : salon?.is_planning_finalized
                ? 'Planning validé'
                : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {creatingMeetingDetails && (
        <Modal
          show={showCreateMeetingModal}
          onHide={() => setShowCreateMeetingModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Créer RDV - {creatingMeetingDetails.slotStart} -{' '}
              {creatingMeetingDetails.slotEnd}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Participant Entreprise</Form.Label>
              <Form.Select
                value={newMeetingCompanyP}
                onChange={e => setNewMeetingCompanyP(e.target.value)}
              >
                <option value="">-- Aucun --</option>
                {companyParticipantsByCompany &&
                  Object.entries(companyParticipantsByCompany).map(
                    ([companyName, participants]) => (
                      <optgroup label={companyName} key={companyName}>
                        {participants.map(p => (
                          <option key={`create-comp-opt-${p.id}`} value={p.id}>
                            {formatParticipantNameForSelect(p, 'company')}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Participant Partenaire</Form.Label>
              <Form.Select
                value={newMeetingPartnerP}
                onChange={e => setNewMeetingPartnerP(e.target.value)}
              >
                <option value="">-- Aucun --</option>
                {partnerParticipantsByPartner &&
                  Object.entries(partnerParticipantsByPartner).map(
                    ([partnerName, participants]) => (
                      <optgroup label={partnerName} key={partnerName}>
                        {participants.map(p => (
                          <option key={`create-part-opt-${p.id}`} value={p.id}>
                            {formatParticipantNameForSelect(p, 'partner')}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowCreateMeetingModal(false)}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateMeeting}
              disabled={isCreatingMeeting || salon?.is_planning_finalized} // Désactiver si en cours de création ou si le planning est validé
            >
              {isCreatingMeeting
                ? 'Création...'
                : salon?.is_planning_finalized
                ? 'Planning validé'
                : 'Créer RDV'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );

  if (isFullScreen) {
    return (
      <Modal show={isFullScreen} onHide={toggleFullScreen} fullscreen={true}>
        <Modal.Header closeButton>
          <Modal.Title>
            Rendez-vous (Plein écran) - {salon.name || 'Salon'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1rem' }}>
          {cardContent}
          {modalsContent}{' '}
          {/* Inclure les modals à l'intérieur du modal plein écran */}
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Card className="mt-3">
        <Card.Body>{cardContent}</Card.Body>
      </Card>
      {modalsContent}{' '}
      {/* Afficher les modals normalement hors mode plein écran */}
    </>
  );
};

MeetingsCard.propTypes = {
  salon: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    date: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    slot_duration: PropTypes.number,
    lunch_break_start_time: PropTypes.string,
    lunch_break_duration: PropTypes.number,
    can_schedule: PropTypes.bool,
    closing_date: PropTypes.string,
    registration_end_date: PropTypes.string,
    is_planning_finalized: PropTypes.bool,
    meetings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        start_time: PropTypes.string.isRequired,
        end_time: PropTypes.string.isRequired,
        company_participants: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            firstname: PropTypes.string,
            lastname: PropTypes.string,
            company: PropTypes.shape({ name: PropTypes.string })
          })
        ),
        partner_participants: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            firstname: PropTypes.string,
            lastname: PropTypes.string,
            partner: PropTypes.shape({ name: PropTypes.string })
          })
        )
      })
    ),
    unmet_company_wishes: PropTypes.array,
    unmet_partner_wishes: PropTypes.array,
    participant_conflicts: PropTypes.array,
    company_participants: PropTypes.array,
    partner_participants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        partner: PropTypes.shape({ name: PropTypes.string })
      })
    )
  }).isRequired,
  onEditMeeting: PropTypes.func,
  onMeetingUpdated: PropTypes.func
};

export default MeetingsCard;
